import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ParticleField from "./ParticleField";

const BASE_GRAVITY = 0.17;
const FLAP = -11.7;
const PIPE_WIDTH = 100;
const BASE_GAP = 300;

// Same grid background as the rest of the arcade
const GridBackground = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const TILE = 36;
    let frame = 0;
    let raf;
    const draw = () => {
      raf = requestAnimationFrame(draw);
      frame++;
      const W = canvas.width; const H = canvas.height;
      ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "rgba(0,245,255,0.045)"; ctx.lineWidth = 0.5;
      const cols = Math.ceil(W / TILE); const rows = Math.ceil(H / TILE);
      for (let i = 0; i <= cols; i++) { ctx.beginPath(); ctx.moveTo(i * TILE, 0); ctx.lineTo(i * TILE, H); ctx.stroke(); }
      for (let j = 0; j <= rows; j++) { ctx.beginPath(); ctx.moveTo(0, j * TILE); ctx.lineTo(W, j * TILE); ctx.stroke(); }
      if (frame % 8 === 0) {
        const rx = Math.floor(Math.random() * cols) * TILE;
        const ry = Math.floor(Math.random() * rows) * TILE;
        ctx.fillStyle = `rgba(0,255,136,${Math.random() * 0.06})`;
        ctx.fillRect(rx + 1, ry + 1, TILE - 2, TILE - 2);
      }
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, display: "block" }} />;
};

// Returns responsive game config based on screen width
const getResponsiveConfig = () => {
  const isMobile = window.innerWidth < 600;
  const isTablet = window.innerWidth < 900;

  const pipeSpeed = isMobile ? 1.4 : isTablet ? 1.7 : 2;

  // On desktop the original 1500ms feels right. On smaller screens we calculate
  // dynamically so there's always ~1.8 screen-widths of breathing room between spawns.
  let pipeInterval;
  if (!isMobile && !isTablet) {
    pipeInterval = 1500;
  } else {
    const screenW = window.innerWidth;
    const targetGapPx = screenW * 1.8;
    const dynamic = Math.round((targetGapPx / pipeSpeed) * (1000 / 60));
    pipeInterval = Math.max(3000, Math.min(3500, dynamic));
  }

  return {
    pipeGap: isMobile ? 210 : isTablet ? 255 : BASE_GAP,
    pipeSpeed,
    pipeInterval,
    gravity: isMobile ? 0.13 : BASE_GRAVITY,
    pipeWidth: isMobile ? 65 : PIPE_WIDTH,
    minGap: isMobile ? 130 : 100,
    // Flap impulse — softer on mobile so a quick tap doesn't rocket the bird
    flap: isMobile ? -7.5 : isTablet ? -9.5 : FLAP,
    // Hard cap on upward velocity so rapid taps can't send it off-screen
    maxUpVelocity: isMobile ? -8 : isTablet ? -10 : -13,
  };
};

export default function FlappyEgg({ onExit }) {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState("start");
  const [score, setScore] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const scoreRef = useRef(0);
  const isNewBest = useRef(false);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("flappy-leaderboard") || "[]");
    setLeaderboard(stored);
  }, []);

  useEffect(() => {
    if (gameState !== "playing") return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);

    // Pull responsive values fresh when the game starts (after resize)
    const cfg = getResponsiveConfig();

    let gravity = cfg.gravity;
    let pipeGap = cfg.pipeGap;
    let pipeSpeed = cfg.pipeSpeed;
    let pipeInterval = cfg.pipeInterval;
    let pipeWidth = cfg.pipeWidth;
    let impossibleMode = false;
    let lastPipe = 0;
    let animId;
    let glitchFrames = 0;

    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const playBeep = (freq = 440, duration = 0.08, type = "square") => {
      try {
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.type = type;
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.08, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + duration);
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.start();
        osc.stop(audioCtx.currentTime + duration);
      } catch (e) {}
    };
    // Haptic feedback — silently no-ops on unsupported browsers / iOS WebView
    const vibrate = (pattern) => { try { navigator.vibrate?.(pattern); } catch (e) {} };

    const TILE = 36;
    const bird = { x: canvas.width * 0.25, y: canvas.height / 2, velocity: 0, radius: 12 };
    let pipes = [];
    let frameCount = 0;

    const spawnPipe = () => {
      const topHeight = Math.random() * (canvas.height - pipeGap - 100) + 50;
      pipes.push({ x: canvas.width, top: topHeight, bottom: topHeight + pipeGap, passed: false });
    };

    const collision = () => {
      if (bird.y + bird.radius > canvas.height || bird.y - bird.radius < 0) return true;
      for (let p of pipes) {
        if (bird.x + bird.radius > p.x && bird.x - bird.radius < p.x + pipeWidth) {
          if (bird.y - bird.radius < p.top || bird.y + bird.radius > p.bottom) return true;
        }
      }
      return false;
    };

    const update = () => {
      frameCount++;
      const W = canvas.width; const H = canvas.height;
      ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, H);

      // Grid
      ctx.strokeStyle = "rgba(0,245,255,0.04)"; ctx.lineWidth = 0.5;
      const cols = Math.ceil(W / TILE); const rows = Math.ceil(H / TILE);
      for (let i = 0; i <= cols; i++) { ctx.beginPath(); ctx.moveTo(i * TILE, 0); ctx.lineTo(i * TILE, H); ctx.stroke(); }
      for (let j = 0; j <= rows; j++) { ctx.beginPath(); ctx.moveTo(0, j * TILE); ctx.lineTo(W, j * TILE); ctx.stroke(); }
      // Random cell flicker
      if (frameCount % 8 === 0) {
        const rx = Math.floor(Math.random() * cols) * TILE;
        const ry = Math.floor(Math.random() * rows) * TILE;
        ctx.fillStyle = `rgba(255,0,85,${Math.random() * 0.06})`;
        ctx.fillRect(rx + 1, ry + 1, TILE - 2, TILE - 2);
      }

      // Physics
      bird.velocity += gravity;
      bird.y += bird.velocity;

      if (Date.now() - lastPipe > pipeInterval) { spawnPipe(); lastPipe = Date.now(); }

      // Update pipes
      pipes.forEach((p) => {
        p.x -= pipeSpeed;
        if (!p.passed && p.x + pipeWidth < bird.x) {
          p.passed = true;
          scoreRef.current += 1;
          setScore(scoreRef.current);
          playBeep(700, 0.08, "triangle");
          gravity += 0.015;
          pipeSpeed += 0.07;
          pipeGap = Math.max(cfg.minGap, pipeGap - 2);
          // Interval also shrinks slightly as difficulty ramps
          pipeInterval = Math.max(cfg.pipeInterval * 0.6, pipeInterval - 10);
          if (scoreRef.current >= 25 && !impossibleMode) {
            impossibleMode = true;
            gravity *= 1.4; pipeSpeed *= 1.7; pipeGap = cfg.minGap;
            glitchFrames = 30;
          }
        }
      });
      pipes = pipes.filter((p) => p.x + pipeWidth > 0);

      // Draw pipes
      pipes.forEach((p) => {
        ctx.fillStyle = "rgba(0,245,255,0.12)";
        ctx.fillRect(p.x, 0, pipeWidth, p.top);
        ctx.fillRect(p.x, p.bottom, pipeWidth, H - p.bottom);

        ctx.strokeStyle = "#00f5ff";
        ctx.shadowColor = "#00f5ff";
        ctx.shadowBlur = 8;
        ctx.lineWidth = 1;
        ctx.strokeRect(p.x, 0, pipeWidth, p.top);
        ctx.strokeRect(p.x, p.bottom, pipeWidth, H - p.bottom);
        ctx.shadowBlur = 0;

        ctx.fillStyle = "#00f5ff";
        ctx.fillRect(p.x, p.top - 2, pipeWidth, 2);
        ctx.fillRect(p.x, p.bottom, pipeWidth, 2);
      });

      // Bird
      const bx = Math.round(bird.x - bird.radius);
      const by = Math.round(bird.y - bird.radius);
      const bs = bird.radius * 2;
      ctx.shadowColor = "#ff0055";
      ctx.shadowBlur = 20;
      ctx.fillStyle = "#ff0055";
      ctx.fillRect(bx, by, bs, bs);
      ctx.fillStyle = "rgba(255,255,255,0.4)";
      ctx.fillRect(bx + 3, by + 3, 4, 4);
      ctx.shadowBlur = 0;

      if (glitchFrames > 0) {
        ctx.fillStyle = `rgba(255,0,85,${Math.random() * 0.12})`;
        ctx.fillRect(0, 0, W, H);
        glitchFrames--;
      }

      if (collision()) { endGame(); return; }
      animId = requestAnimationFrame(update);
    };

    const flap = () => {
      bird.velocity = Math.max(cfg.maxUpVelocity, bird.velocity + cfg.flap);
      playBeep(400, 0.06, "square");
      vibrate(30); // short tap buzz
    };

    const endGame = () => {
      cancelAnimationFrame(animId);
      const stored = JSON.parse(localStorage.getItem("flappy-leaderboard") || "[]");
      isNewBest.current = stored.length === 0 || scoreRef.current > stored[0];
      const updated = [...stored, scoreRef.current].sort((a, b) => b - a).slice(0, 5);
      localStorage.setItem("flappy-leaderboard", JSON.stringify(updated));
      setLeaderboard(updated);
      playBeep(120, 0.4, "sawtooth");
      vibrate([80, 40, 80, 40, 200]); // death rattle pattern
      setGameState("gameover");
    };

    const onKey = (e) => { if (e.code === "Space") { e.preventDefault(); flap(); } };
    window.addEventListener("pointerdown", flap);
    window.addEventListener("keydown", onKey);
    update();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("pointerdown", flap);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", resize);
      audioCtx.close();
    };
  }, [gameState]);

  const startGame = () => { scoreRef.current = 0; setScore(0); isNewBest.current = false; setGameState("playing"); };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "#000", overflow: "hidden" }}>
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, display: "block" }} />

      {/* Live HUD during play */}
      {gameState === "playing" && (
        <div style={{
          position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)",
          textAlign: "center", pointerEvents: "none", fontFamily: "monospace",
          textShadow: "0 0 10px #ff0055",
        }}>
          <div style={{ fontSize: 10, letterSpacing: 6, color: "#ff0055", opacity: 0.5 }}>FLAPPY BOX</div>
          <div style={{ fontSize: 28, color: "#fff", marginTop: 4, letterSpacing: 4 }}>{score}</div>
        </div>
      )}

      {/* Start screen */}
      <AnimatePresence>
        {gameState === "start" && (
          <motion.div
            key="start"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{
              position: "absolute", inset: 0,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              fontFamily: "monospace", color: "#fff",
            }}
          >
            <GridBackground />
            <div className="absolute inset-0 pointer-events-none" />

            <div
              className="absolute top-1/4 left-1/3 w-[600px] h-[600px] rounded-full pointer-events-none hidden dark:block"
              style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.07) 0%, transparent 70%)' }}
            />
            <div
              className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none hidden dark:block"
              style={{ background: 'radial-gradient(circle, hsl(var(--secondary) / 0.05) 0%, transparent 70%)' }}
            />
            <div
              className="absolute right-[15%] top-1/2 w-[450px] h-[450px] rounded-full pointer-events-none block dark:hidden"
              style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.08) 0%, transparent 65%)', transform: 'translateY(-50%)' }}
            />
            <div className="absolute inset-0 bg-grid pointer-events-none dark:opacity-40 opacity-15" />
            <div className="absolute inset-0">
              <ParticleField />
            </div>

            <div style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
              <motion.div
                initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div style={{ fontSize: 11, letterSpacing: 10, color: "#ff0055", opacity: 0.5, marginBottom: 10, textShadow: "0 0 10px #ff0055" }}>
                  SYSTEM / ARCADE
                </div>
                <div style={{ fontSize: 36, letterSpacing: 10, color: "#fff", fontWeight: 900, textShadow: "0 0 30px rgba(255,0,85,0.5)" }}>
                  FLAPPY BOX
                </div>
                <motion.div
                  initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.4, duration: 0.6 }}
                  style={{ height: 1, background: "linear-gradient(90deg, transparent, #ff0055, transparent)", marginTop: 12, transformOrigin: "center" }}
                />
                <div style={{ fontSize: 10, letterSpacing: 3, color: "rgba(255, 255, 255, 0.53)", marginTop: 12 }}>
                  ONE TAP FROM OBLIVION
                </div>
              </motion.div>

              {leaderboard.length > 0 && (
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                  style={{ marginTop: 32, marginBottom: 8 }}
                >
                  <div style={{ fontSize: 9, letterSpacing: 4, color: "rgba(255,255,255,0.5)", marginBottom: 12 }}>
                    TOP SCORES
                  </div>
                  {leaderboard.map((s, i) => (
                    <div key={i} style={{
                      display: "flex", justifyContent: "space-between", gap: 40,
                      fontSize: 11, letterSpacing: 2, marginBottom: 6,
                      color: i === 0 ? "#ff0055" : "rgba(255,255,255,0.5)",
                      textShadow: i === 0 ? "0 0 8px #ff0055" : "none",
                    }}>
                      <span style={{ opacity: 0.5 }}>{String(i + 1).padStart(2, "0")}</span>
                      <span>{s}</span>
                    </div>
                  ))}
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
                style={{ marginTop: 36, display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}
              >
                <button
                  onClick={startGame}
                  style={{
                    padding: "14px 40px", background: "transparent",
                    border: "1px solid #ff0055", color: "#ff0055",
                    fontFamily: "monospace", fontSize: 13, letterSpacing: 4,
                    cursor: "pointer", textShadow: "0 0 8px #ff0055",
                    boxShadow: "0 0 16px rgba(255,0,85,0.3)",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,0,85,0.1)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  ▶ FLY
                </button>

                <div style={{ fontSize: 9, letterSpacing: 3, color: "rgba(255,255,255,0.5)" }}>
                  SPACE / TAP TO FLAP
                </div>

                <button
                  onClick={onExit}
                  style={{
                    marginTop: 8, background: "transparent", border: "none",
                    fontFamily: "monospace", fontSize: 10, letterSpacing: 4,
                    color: "rgba(255,255,255,0.25)", cursor: "pointer",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = "rgba(255,255,255,0.7)"}
                  onMouseLeave={(e) => e.currentTarget.style.color = "rgba(255,255,255,0.5)"}
                >
                  ← BACK
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Game Over screen */}
      <AnimatePresence>
        {gameState === "gameover" && (
          <motion.div
            key="gameover"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{
              position: "absolute", inset: 0, background: "rgba(0,0,0,0.88)",
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", color: "#fff", fontFamily: "monospace",
            }}
          >
            <motion.h1
              initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              style={{ fontSize: 36, marginBottom: 8, color: "#ff0055", textShadow: "0 0 24px #ff0055", letterSpacing: 6 }}
            >
              SYSTEM FAILURE
            </motion.h1>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              style={{ textAlign: "center" }}
            >
              <p style={{ opacity: 0.5, fontSize: 11, letterSpacing: 3, marginBottom: 6 }}>FINAL SCORE</p>
              <p style={{ fontSize: 40, letterSpacing: 4, color: "#fff", marginBottom: 16 }}>{scoreRef.current}</p>

              {isNewBest.current && scoreRef.current > 0 && (
                <motion.p
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  style={{ color: "#ff0055", fontSize: 11, letterSpacing: 4, textShadow: "0 0 10px #ff0055", marginBottom: 16 }}
                >
                  ◆ NEW BEST ◆
                </motion.p>
              )}

              {leaderboard.length > 0 && (
                <div style={{ marginBottom: 28 }}>
                  <div style={{ fontSize: 9, letterSpacing: 4, color: "rgba(255,255,255,0.2)", marginBottom: 10 }}>
                    TOP SCORES
                  </div>
                  {leaderboard.map((s, i) => (
                    <div key={i} style={{
                      display: "flex", justifyContent: "space-between", gap: 48,
                      fontSize: 11, letterSpacing: 2, marginBottom: 5,
                      color: s === scoreRef.current && i === leaderboard.indexOf(scoreRef.current) ? "#ff0055" : "rgba(255,255,255,0.35)",
                    }}>
                      <span style={{ opacity: 0.4 }}>{String(i + 1).padStart(2, "0")}</span>
                      <span>{s}</span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: "flex", gap: 12 }}>
                <button
                  onClick={startGame}
                  style={{
                    padding: "12px 28px", background: "transparent", border: "1px solid #00f5ff",
                    color: "#00f5ff", fontFamily: "monospace", fontSize: 12, letterSpacing: 3,
                    cursor: "pointer", textShadow: "0 0 8px #00f5ff", boxShadow: "0 0 12px rgba(0,245,255,0.25)",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,245,255,0.08)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  RESTART
                </button>
                {onExit && (
                  <button
                    onClick={onExit}
                    style={{
                      padding: "12px 28px", background: "transparent", border: "1px solid #ff0055",
                      color: "#ff0055", fontFamily: "monospace", fontSize: 12, letterSpacing: 3,
                      cursor: "pointer", textShadow: "0 0 8px #ff0055", boxShadow: "0 0 12px rgba(255,0,85,0.25)",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,0,85,0.08)"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    EXIT
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}